/**
 * School Partner Dashboard — same layout pattern as Agent account.
 * Dashboard Home (SchoolOverview), Manual Payments, Receipts, Enroll Student, Account Settings.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SchoolOverview from './SchoolOverview';
import Payments from '../tabs/Payments';
import SchoolEnrollStudent from './SchoolEnrollStudent';
import SchoolAccountSettings from './SchoolAccountSettings';
import LanguageSwitcher from '../common/LanguageSwitcher';
import '../Sidebar.css';
import './SchoolDashboard.css';

const SchoolDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'navigation']);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar toggle
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = user.name || user.email || 'Partner';

  const SCHOOL_TABS = [
    { id: 'dashboard', label: t('navigation:dashboard'), icon: 'dashboard' },
    { id: 'manual-payments', label: t('navigation:payments.tabs.manualPayments'), icon: 'payments' },
    { id: 'receipts', label: t('navigation:payments.tabs.receipts'), icon: 'receipt' },
    { id: 'enroll-student', label: t('common:school.enrollStudent'), icon: 'person_add' },
    { id: 'account-settings', label: t('common:menu.accountSettings'), icon: 'settings' },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('adminToken');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="App school-dashboard-app">
      <div className="dashboard-container">
        {/* School sidebar — simplified, no admin sections */}
        {/* Mobile overlay/backdrop */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay school-sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <aside className={`sidebar school-sidebar ${sidebarOpen ? 'school-sidebar--open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <span className="material-icons">school</span>
              <h2>Syllera</h2>
            </div>
            <div className="profile-section school-profile">
              <span className="material-icons">business</span>
              <div>
                <p className="profile-name">{displayName}</p>
                <p className="profile-role">{t('common:school.partnerRole')}</p>
              </div>
            </div>
            <div className="school-language-switch">
              <LanguageSwitcher className="school-language-switcher" />
            </div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-section">
              <ul className="nav-list">
                {SCHOOL_TABS.map((tab) => (
                  <li key={tab.id} className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <span className="nav-icon">
                        <span className="material-icons">{tab.icon}</span>
                      </span>
                      <span className="nav-text">{tab.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
          <div className="sidebar-footer">
            <ul className="nav-list">
              <li className="nav-item">
                <button type="button" className="nav-link" onClick={handleLogout}>
                  <span className="nav-icon">
                    <span className="material-icons">logout</span>
                  </span>
                  <span className="nav-text">{t('common:menu.logout')}</span>
                </button>
              </li>
            </ul>
          </div>
        </aside>

        <main className="main-content school-main-content">
          {/* Mobile header (admin-style hamburger) */}
          <div className="school-mobile-topbar" role="banner">
            <button
              type="button"
              className="mobile-menu-toggle"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={sidebarOpen || false}
            >
              <i className="material-icons">{sidebarOpen ? 'close' : 'menu'}</i>
            </button>
            <div className="school-mobile-topbar__title">
              {t('common:school.partnerRole')}
            </div>
          </div>

          {activeTab === 'dashboard' && (
            <SchoolOverview onTabChange={handleTabChange} />
          )}
          {activeTab === 'manual-payments' && (
            <div className="school-tab-content school-payments-wrapper">
              <Payments singleTab="manual-payments" />
            </div>
          )}
          {activeTab === 'receipts' && (
            <div className="school-tab-content school-payments-wrapper">
              <Payments singleTab="receipts" />
            </div>
          )}
          {activeTab === 'enroll-student' && (
            <SchoolEnrollStudent />
          )}
          {activeTab === 'account-settings' && (
            <SchoolAccountSettings />
          )}
        </main>
      </div>
    </div>
  );
};

export default SchoolDashboard;
